import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  Users,
  Search,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Shield,
  Edit,
  X,
  Trash2,
  AlertTriangle,
  Save,
  Layout,
  Activity,
  DollarSign,
  UserPlus,
  CreditCard,
  MessageCircle,
  Filter,
  Wifi,
  WifiOff,
  Clock,
  TrendingDown,
  TrendingUp,
  Target,
  RefreshCw,
  BarChart2
} from 'lucide-react';
import AnalysisStyles from '../components/AnalysisStyles';
import type { SetupTracking } from '../lib/useBotAPI';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.asistentetradingia.com';
const API_KEY = import.meta.env.VITE_API_KEY || '';

interface User {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string;
  stats: {
    total_analyses: number;
    today_analyses: number;
    total_trades: number;
    win_rate: number;
    user_level: string;
    is_blocked: boolean;
    analysis_limit: number;
  };
  subscription_status?: string;
  has_telegram?: boolean;
  is_ambassador?: boolean;
}

interface EditUserModal {
  userId: string;
  email: string;
  analysisLimit: number;
  userLevel: string;
  isBlocked: boolean;
}

interface DeleteUserModal {
  userId: string;
  email: string;
}

interface Metrics {
  totalUsers: number;
  activeSubscribers: number;
  mrr: number;
  newThisMonth: number;
}

interface FeedStatus {
  is_live: boolean;
  timestamp: string;
}

interface SetupEntry {
  id: string;
  timestamp: string;
  direction: string;
  score: number;
  status: string;
  outcome?: string;
  entry_min: number;
  entry_max: number;
  pnl_usd?: number;
}

type FilterType = 'all' | 'active' | 'free' | 'telegram' | 'ambassadors' | 'blocked';
type TradingPeriod = '7' | '30' | 'all';
type TradingOutcome = 'all' | 'won' | 'lost' | 'expired';

interface HistoryDay {
  date: string;
  total_setups: number;
  won: number;
  lost: number;
  timeout: number;
  expired: number;
  pending: number;
  total_usd: number;
  setups: SetupTracking[];
}

interface HistorySummary {
  total_setups: number;
  won: number;
  lost: number;
  timeout: number;
  expired: number;
  pending: number;
  total_usd: number;
  closed: number;
  win_rate: number;
  profit_factor: number;
  gross_wins: number;
  gross_losses: number;
}

interface HistoryResponse {
  days: HistoryDay[];
  summary: HistorySummary;
}

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingUser, setEditingUser] = useState<EditUserModal | null>(null);
  const [deletingUser, setDeletingUser] = useState<DeleteUserModal | null>(null);
  const [activeTab, setActiveTab] = useState<'users' | 'styles' | 'bot' | 'trading'>('users');
  const [metrics, setMetrics] = useState<Metrics>({ totalUsers: 0, activeSubscribers: 0, mrr: 0, newThisMonth: 0 });
  const [feedStatus, setFeedStatus] = useState<FeedStatus | null>(null);
  const [todaySetups, setTodaySetups] = useState<SetupEntry[]>([]);
  const [botLoading, setBotLoading] = useState(false);
  const [tradingHistory, setTradingHistory] = useState<HistoryResponse | null>(null);
  const [tradingLoading, setTradingLoading] = useState(false);
  const [tradingPeriod, setTradingPeriod] = useState<TradingPeriod>('30');
  const [tradingOutcome, setTradingOutcome] = useState<TradingOutcome>('all');
  const itemsPerPage = 10;

  useEffect(() => {
    checkAdminAccess();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchUsers();
      fetchMetrics();
    }
  }, [page, search, loading]);

  useEffect(() => {
    if (activeTab === 'bot') {
      fetchBotStatus();
      const interval = setInterval(fetchBotStatus, 30000);
      return () => clearInterval(interval);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'trading') {
      fetchTradingHistory();
    }
  }, [activeTab, tradingPeriod]);

  const checkAdminAccess = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate('/login'); return; }
      const { data } = await supabase.from('admin_users').select('id').eq('id', user.id);
      if (!data || data.length === 0) { navigate('/dashboard'); return; }
      setLoading(false);
    } catch (err) {
      console.error('Error checking admin access:', err);
      navigate('/dashboard');
    }
  };

  const fetchMetrics = async () => {
    try {
      // Total users
      const { count: totalCount } = await supabase.from('profiles').select('id', { count: 'exact', head: true });

      // Active subscribers
      const now = new Date().toISOString();
      const { count: subCount } = await supabase
        .from('subscriptions')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'active')
        .gt('current_period_end', now);

      // New this month
      const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString();
      const { count: newCount } = await supabase
        .from('profiles')
        .select('id', { count: 'exact', head: true })
        .gt('created_at', thirtyDaysAgo);

      const activeSubscribers = subCount || 0;
      setMetrics({
        totalUsers: totalCount || 0,
        activeSubscribers,
        mrr: activeSubscribers * 49,
        newThisMonth: newCount || 0,
      });
    } catch (err) {
      console.error('Error fetching metrics:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      setError('');
      const { data: usersData, error: usersError } = await supabase.rpc('get_all_users', {
        search_query: search, page_number: page, items_per_page: itemsPerPage,
      });
      if (usersError) throw usersError;

      const { count, error: countError } = await supabase.rpc('get_users_count', { search_query: search });
      if (countError) throw countError;

      const userIds = (usersData || []).map((u: any) => u.id);

      // Fetch enrichment data in parallel
      const [statsResults, subsResult, profilesResult, ambassadorsResult] = await Promise.all([
        Promise.all((usersData || []).map((user: any) =>
          supabase.rpc('get_user_stats', { user_id_param: user.id }).then(r => ({ id: user.id, stats: r.data?.[0] }))
        )),
        userIds.length > 0
          ? supabase.from('subscriptions').select('user_id, status, current_period_end').in('user_id', userIds)
          : { data: [] },
        userIds.length > 0
          ? supabase.from('profiles').select('id, telegram_id').in('id', userIds)
          : { data: [] },
        userIds.length > 0
          ? supabase.from('ambassador_accounts').select('user_id').in('user_id', userIds)
          : { data: [] },
      ]);

      const now = new Date();
      const subsMap = new Map<string, string>();
      for (const s of (subsResult.data || [])) {
        const isActive = s.status === 'active' && new Date(s.current_period_end) > now;
        const current = subsMap.get(s.user_id);
        if (isActive) subsMap.set(s.user_id, 'active');
        else if (!current) subsMap.set(s.user_id, s.status === 'canceled' ? 'canceled' : 'inactive');
      }

      const telegramSet = new Set((profilesResult.data || []).filter((p: any) => p.telegram_id).map((p: any) => p.id));
      const ambassadorSet = new Set((ambassadorsResult.data || []).map((a: any) => a.user_id));
      const statsMap = new Map(statsResults.map(s => [s.id, s.stats]));

      const enrichedUsers: User[] = (usersData || []).map((user: any) => ({
        ...user,
        stats: statsMap.get(user.id) || {},
        subscription_status: subsMap.get(user.id) || 'free',
        has_telegram: telegramSet.has(user.id),
        is_ambassador: ambassadorSet.has(user.id),
      }));

      setUsers(enrichedUsers);
      setTotalPages(Math.ceil((count || 0) / itemsPerPage));
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Error al cargar los usuarios');
    } finally {
      setLoading(false);
    }
  };

  const fetchBotStatus = async () => {
    setBotLoading(true);
    try {
      const [feedRes, setupsRes] = await Promise.all([
        fetch(`${API_BASE}/api/setups`, { headers: { 'X-API-Key': API_KEY } }).then(r => r.json()).catch(() => null),
        fetch(`${API_BASE}/api/setups-tracking`, { headers: { 'X-API-Key': API_KEY } }).then(r => r.json()).catch(() => null),
      ]);

      if (feedRes) {
        setFeedStatus({ is_live: feedRes.feed_live ?? false, timestamp: new Date().toISOString() });
      }
      if (setupsRes?.setups) {
        setTodaySetups(setupsRes.setups);
      }
    } catch (err) {
      console.error('Error fetching bot status:', err);
    } finally {
      setBotLoading(false);
    }
  };

  const fetchTradingHistory = async () => {
    setTradingLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const days = tradingPeriod === 'all' ? 365 : parseInt(tradingPeriod);
      const res = await fetch(`${API_BASE}/api/admin/setups-history?days=${days}`, {
        headers: {
          'X-API-Key': API_KEY,
          'Authorization': `Bearer ${session.access_token}`,
        },
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data: HistoryResponse = await res.json();
      setTradingHistory(data);
    } catch (err) {
      console.error('Error fetching trading history:', err);
    } finally {
      setTradingLoading(false);
    }
  };

  // Filtered setups for trading tab
  const filteredTradingSetups = useMemo(() => {
    if (!tradingHistory) return [];
    const allSetups: (SetupTracking & { date: string })[] = [];
    for (const day of tradingHistory.days) {
      for (const s of day.setups) {
        if (s.setup_type !== 'live') continue;
        allSetups.push({ ...s, date: day.date });
      }
    }
    if (tradingOutcome === 'all') return allSetups;
    return allSetups.filter(s => {
      if (tradingOutcome === 'won') return s.outcome === 'won' || (s.outcome === 'timeout' && (s.pnl_usd || 0) > 0);
      if (tradingOutcome === 'lost') return s.outcome === 'lost' || s.outcome === 'ambiguous_intrabar' || (s.outcome === 'timeout' && (s.pnl_usd || 0) < 0);
      if (tradingOutcome === 'expired') return s.outcome === 'expired';
      return true;
    });
  }, [tradingHistory, tradingOutcome]);

  // Filtered users (local filter on loaded data)
  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      switch (filter) {
        case 'active': return u.subscription_status === 'active';
        case 'free': return u.subscription_status === 'free';
        case 'telegram': return u.has_telegram;
        case 'ambassadors': return u.is_ambassador;
        case 'blocked': return u.stats?.is_blocked;
        default: return true;
      }
    });
  }, [users, filter]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => { setSearch(e.target.value); setPage(1); };
  const handlePageChange = (n: number) => { if (n >= 1 && n <= totalPages) setPage(n); };

  const handleEditUser = (user: User) => {
    setEditingUser({
      userId: user.id, email: user.email,
      analysisLimit: user.stats.analysis_limit, userLevel: user.stats.user_level, isBlocked: user.stats.is_blocked,
    });
  };

  const handleDeleteUser = (user: User) => { setDeletingUser({ userId: user.id, email: user.email }); };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    try {
      setLoading(true); setError('');
      const { error } = await supabase.rpc('update_user_limits', {
        user_id_param: editingUser.userId, new_limit: editingUser.analysisLimit,
        new_level: editingUser.userLevel, block_status: editingUser.isBlocked,
      });
      if (error) throw error;
      setSuccess('Usuario actualizado correctamente'); setEditingUser(null); fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating user:', err);
      setError('Error al actualizar el usuario. Por favor, intentalo de nuevo.');
    } finally { setLoading(false); }
  };

  const handleConfirmDelete = async () => {
    if (!deletingUser) return;
    try {
      setError('');
      const { error } = await supabase.rpc('delete_user_safely', { user_id_param: deletingUser.userId });
      if (error) throw error;
      setSuccess('Usuario eliminado correctamente'); setDeletingUser(null); fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Error al eliminar el usuario');
    }
  };

  const subBadge = (status?: string) => {
    switch (status) {
      case 'active': return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-400">Activo</span>;
      case 'canceled': return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-500/20 text-red-400">Cancelado</span>;
      default: return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-500/20 text-gray-400">Gratuito</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center">
            <Shield className="h-8 w-8 text-primary mr-3" />
            Panel de Administracion
          </h1>
          <p className="mt-2 text-gray-400">
            Gestiona usuarios y configuraciones del sistema
          </p>
        </div>

        {/* FASE 2: Metrics Dashboard */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card p-4">
            <div className="flex items-center justify-between">
              <Users className="h-5 w-5 text-gray-400" />
            </div>
            <p className="text-2xl font-bold text-white mt-2">{metrics.totalUsers}</p>
            <p className="text-xs text-gray-400 mt-1">Total usuarios</p>
          </div>
          <div className="card p-4">
            <div className="flex items-center justify-between">
              <CreditCard className="h-5 w-5 text-green-400" />
            </div>
            <p className="text-2xl font-bold text-green-400 mt-2">{metrics.activeSubscribers}</p>
            <p className="text-xs text-gray-400 mt-1">Suscriptores activos</p>
          </div>
          <div className="card p-4">
            <div className="flex items-center justify-between">
              <DollarSign className="h-5 w-5 text-primary" />
            </div>
            <p className="text-2xl font-bold text-primary mt-2">{metrics.mrr.toLocaleString('es-ES')}€</p>
            <p className="text-xs text-gray-400 mt-1">MRR estimado</p>
          </div>
          <div className="card p-4">
            <div className="flex items-center justify-between">
              <UserPlus className="h-5 w-5 text-blue-400" />
            </div>
            <p className="text-2xl font-bold text-blue-400 mt-2">{metrics.newThisMonth}</p>
            <p className="text-xs text-gray-400 mt-1">Nuevos (30 dias)</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-900/50 rounded-lg p-4 flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-red-500">{error}</p>
          </div>
        )}
        {success && (
          <div className="bg-green-900/20 border border-green-900/50 rounded-lg p-4 flex items-center">
            <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
            <p className="text-green-500">{success}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex space-x-4 border-b border-white/10">
          {([
            { key: 'users' as const, icon: Users, label: 'Usuarios' },
            { key: 'trading' as const, icon: BarChart2, label: 'Trading' },
            { key: 'bot' as const, icon: Activity, label: 'Bot' },
            { key: 'styles' as const, icon: Layout, label: 'Estilos' },
          ]).map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`pb-4 px-2 relative ${activeTab === tab.key ? 'text-primary' : 'text-gray-400 hover:text-white'}`}
            >
              <div className="flex items-center space-x-2">
                <tab.icon className="h-5 w-5" />
                <span>{tab.label}</span>
              </div>
              {activeTab === tab.key && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
            </button>
          ))}
        </div>

        {/* TAB: Users */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Search + Filters */}
            <div className="space-y-3">
              <div className="flex-1 relative">
                <input type="text" placeholder="Buscar por email..." value={search} onChange={handleSearch} className="input-field pl-10 w-full" />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              </div>
              {/* FASE 4: Filters */}
              <div className="flex items-center space-x-2 flex-wrap">
                <Filter className="h-4 w-4 text-gray-500" />
                {([
                  { key: 'all' as FilterType, label: 'Todos' },
                  { key: 'active' as FilterType, label: 'Activos' },
                  { key: 'free' as FilterType, label: 'Gratuitos' },
                  { key: 'telegram' as FilterType, label: 'Con Telegram' },
                  { key: 'ambassadors' as FilterType, label: 'Embajadores' },
                  { key: 'blocked' as FilterType, label: 'Bloqueados' },
                ]).map(f => (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    className={`px-3 py-1 rounded-full text-xs transition-colors ${
                      filter === f.key
                        ? 'bg-primary/20 text-primary border border-primary/30'
                        : 'bg-white/5 text-gray-400 hover:text-white border border-white/10'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Users Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left border-b border-white/10">
                    <th className="pb-3 text-gray-400 text-sm">Email</th>
                    <th className="pb-3 text-gray-400 text-sm">Nivel</th>
                    <th className="pb-3 text-gray-400 text-sm">Analisis</th>
                    <th className="pb-3 text-gray-400 text-sm">Trades</th>
                    <th className="pb-3 text-gray-400 text-sm">WR</th>
                    <th className="pb-3 text-gray-400 text-sm">Suscripcion</th>
                    <th className="pb-3 text-gray-400 text-sm">TG</th>
                    <th className="pb-3 text-gray-400 text-sm">Emb.</th>
                    <th className="pb-3 text-gray-400 text-sm">Estado</th>
                    <th className="pb-3 text-gray-400 text-sm">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-white/5">
                      <td className="py-3 text-white text-sm">{user.email}</td>
                      <td className="py-3 text-white text-sm">{user.stats?.user_level}</td>
                      <td className="py-3 text-white text-sm">{user.stats?.total_analyses || 0}</td>
                      <td className="py-3 text-white text-sm">{user.stats?.total_trades || 0}</td>
                      <td className="py-3 text-white text-sm">{user.stats?.win_rate || 0}%</td>
                      <td className="py-3">{subBadge(user.subscription_status)}</td>
                      <td className="py-3 text-center">
                        {user.has_telegram
                          ? <MessageCircle className="h-4 w-4 text-blue-400 inline" />
                          : <span className="text-gray-600">—</span>
                        }
                      </td>
                      <td className="py-3 text-center">
                        {user.is_ambassador
                          ? <CheckCircle className="h-4 w-4 text-primary inline" />
                          : <span className="text-gray-600">—</span>
                        }
                      </td>
                      <td className="py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          user.stats?.is_blocked ? 'bg-red-500/20 text-red-500' : 'bg-green-500/20 text-green-500'
                        }`}>
                          {user.stats?.is_blocked ? 'Bloq.' : 'OK'}
                        </span>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center space-x-1">
                          <button onClick={() => handleEditUser(user)} className="p-1.5 hover:bg-white/5 rounded-lg transition-colors text-primary" title="Editar">
                            <Edit className="h-4 w-4" />
                          </button>
                          <button onClick={() => handleDeleteUser(user)} className="p-1.5 hover:bg-red-500/20 rounded-lg transition-colors text-red-500" title="Eliminar">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-400">Pagina {page} de {totalPages}</div>
                <div className="flex items-center space-x-2">
                  <button onClick={() => handlePageChange(page - 1)} disabled={page === 1} className="p-2 rounded hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed">
                    <ChevronLeft className="h-5 w-5 text-gray-400" />
                  </button>
                  <button onClick={() => handlePageChange(page + 1)} disabled={page === totalPages} className="p-2 rounded hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed">
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB: Bot Status */}
        {activeTab === 'bot' && (
          <div className="space-y-6">
            {/* Feed Status */}
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center">
                  <Activity className="h-5 w-5 text-primary mr-2" />
                  Estado del LiveFeed
                </h3>
                <button onClick={fetchBotStatus} disabled={botLoading} className="p-2 hover:bg-white/5 rounded-lg transition-colors text-gray-400">
                  <RefreshCw className={`h-4 w-4 ${botLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
              {feedStatus ? (
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    {feedStatus.is_live ? (
                      <>
                        <Wifi className="h-5 w-5 text-green-400" />
                        <span className="text-green-400 font-medium">LIVE</span>
                      </>
                    ) : (
                      <>
                        <WifiOff className="h-5 w-5 text-red-400" />
                        <span className="text-red-400 font-medium">CAIDO</span>
                      </>
                    )}
                  </div>
                  <div className="text-sm text-gray-400">
                    <Clock className="h-3.5 w-3.5 inline mr-1" />
                    Consultado: {new Date(feedStatus.timestamp).toLocaleTimeString('es-ES')}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">Cargando estado...</p>
              )}
              <p className="text-xs text-gray-500 mt-3">Auto-refresh cada 30 segundos</p>
            </div>

            {/* Today's Setups */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-white flex items-center mb-4">
                <Target className="h-5 w-5 text-primary mr-2" />
                Setups del dia
              </h3>
              {todaySetups.length === 0 ? (
                <p className="text-gray-500 text-sm">Sin setups detectados hoy</p>
              ) : (
                <div className="space-y-2">
                  {todaySetups.map((s) => {
                    const time = new Date(s.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
                    const outcomeColors: Record<string, string> = {
                      won: 'bg-green-500/20 text-green-400',
                      lost: 'bg-red-500/20 text-red-400',
                      expired: 'bg-gray-500/20 text-gray-400',
                      active: 'bg-blue-500/20 text-blue-400',
                      pending: 'bg-yellow-500/20 text-yellow-400',
                    };
                    const outcome = s.outcome || s.status;
                    const colorClass = outcomeColors[outcome] || outcomeColors.pending;
                    return (
                      <div key={s.id} className="flex items-center justify-between bg-white/5 rounded-lg px-4 py-3">
                        <div className="flex items-center space-x-3">
                          <span className="text-sm text-gray-400 w-12">{time}</span>
                          {s.direction === 'bearish'
                            ? <TrendingDown className="h-4 w-4 text-red-400" />
                            : <TrendingUp className="h-4 w-4 text-green-400" />
                          }
                          <span className="text-white text-sm">
                            {s.direction === 'bearish' ? 'SHORT' : 'LONG'} {s.entry_min?.toLocaleString('es-ES')}-{s.entry_max?.toLocaleString('es-ES')}
                          </span>
                          <span className="text-xs text-gray-500">Score: {s.score}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          {s.pnl_usd != null && s.pnl_usd !== 0 && (
                            <span className={`text-xs font-medium ${s.pnl_usd > 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {s.pnl_usd > 0 ? '+' : ''}${s.pnl_usd.toLocaleString('es-ES')}
                            </span>
                          )}
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}>
                            {outcome}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB: Trading History */}
        {activeTab === 'trading' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="flex items-center space-x-4 flex-wrap gap-y-2">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-400">Periodo:</span>
                {([
                  { key: '7' as TradingPeriod, label: '7 dias' },
                  { key: '30' as TradingPeriod, label: '30 dias' },
                  { key: 'all' as TradingPeriod, label: 'Todo' },
                ]).map(p => (
                  <button
                    key={p.key}
                    onClick={() => setTradingPeriod(p.key)}
                    className={`px-3 py-1 rounded-full text-xs transition-colors ${
                      tradingPeriod === p.key
                        ? 'bg-primary/20 text-primary border border-primary/30'
                        : 'bg-white/5 text-gray-400 hover:text-white border border-white/10'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-400">Resultado:</span>
                {([
                  { key: 'all' as TradingOutcome, label: 'Todos' },
                  { key: 'won' as TradingOutcome, label: 'Won' },
                  { key: 'lost' as TradingOutcome, label: 'Lost' },
                  { key: 'expired' as TradingOutcome, label: 'Expired' },
                ]).map(o => (
                  <button
                    key={o.key}
                    onClick={() => setTradingOutcome(o.key)}
                    className={`px-3 py-1 rounded-full text-xs transition-colors ${
                      tradingOutcome === o.key
                        ? 'bg-primary/20 text-primary border border-primary/30'
                        : 'bg-white/5 text-gray-400 hover:text-white border border-white/10'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <button onClick={fetchTradingHistory} disabled={tradingLoading} className="p-2 hover:bg-white/5 rounded-lg transition-colors text-gray-400 ml-auto">
                <RefreshCw className={`h-4 w-4 ${tradingLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* Summary Metrics */}
            {tradingHistory?.summary && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="card p-4">
                  <p className="text-xs text-gray-400">Total Trades</p>
                  <p className="text-2xl font-bold text-white mt-1">{tradingHistory.summary.closed}</p>
                  <p className="text-xs text-gray-500 mt-1">{tradingHistory.summary.total_setups} setups detectados</p>
                </div>
                <div className="card p-4">
                  <p className="text-xs text-gray-400">Win Rate</p>
                  <p className="text-2xl font-bold text-green-400 mt-1">{tradingHistory.summary.win_rate}%</p>
                  <p className="text-xs text-gray-500 mt-1">{tradingHistory.summary.won}W / {tradingHistory.summary.lost}L</p>
                </div>
                <div className="card p-4">
                  <p className="text-xs text-gray-400">Profit Factor</p>
                  <p className="text-2xl font-bold text-primary mt-1">
                    {tradingHistory.summary.profit_factor === null || !isFinite(tradingHistory.summary.profit_factor)
                      ? '∞'
                      : tradingHistory.summary.profit_factor.toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    +${tradingHistory.summary.gross_wins.toLocaleString('es-ES')} / -${tradingHistory.summary.gross_losses.toLocaleString('es-ES')}
                  </p>
                </div>
                <div className="card p-4">
                  <p className="text-xs text-gray-400">P&L Acumulado</p>
                  <p className={`text-2xl font-bold mt-1 ${tradingHistory.summary.total_usd >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {tradingHistory.summary.total_usd >= 0 ? '+' : ''}${tradingHistory.summary.total_usd.toLocaleString('es-ES')}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{tradingHistory.days.length} dias con datos</p>
                </div>
              </div>
            )}

            {/* Setups Table */}
            {tradingLoading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
              </div>
            ) : filteredTradingSetups.length === 0 ? (
              <div className="card p-6 text-center text-gray-500">Sin setups para el periodo seleccionado</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left border-b border-white/10">
                      <th className="pb-3 text-gray-400 text-sm">Fecha</th>
                      <th className="pb-3 text-gray-400 text-sm">Sesion</th>
                      <th className="pb-3 text-gray-400 text-sm">Dir.</th>
                      <th className="pb-3 text-gray-400 text-sm">Entrada</th>
                      <th className="pb-3 text-gray-400 text-sm">SL</th>
                      <th className="pb-3 text-gray-400 text-sm">TP</th>
                      <th className="pb-3 text-gray-400 text-sm">R:R</th>
                      <th className="pb-3 text-gray-400 text-sm">Estado</th>
                      <th className="pb-3 text-gray-400 text-sm text-right">P&L USD</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filteredTradingSetups.map((s) => {
                      const outcomeColors: Record<string, string> = {
                        won: 'bg-green-500/20 text-green-400',
                        lost: 'bg-red-500/20 text-red-400',
                        ambiguous_intrabar: 'bg-red-500/20 text-red-400',
                        expired: 'bg-gray-500/20 text-gray-400',
                        timeout: 'bg-yellow-500/20 text-yellow-400',
                        pending: 'bg-blue-500/20 text-blue-400',
                        triggered: 'bg-blue-500/20 text-blue-400',
                      };
                      const outcome = s.outcome || s.status;
                      const colorClass = outcomeColors[outcome] || 'bg-gray-500/20 text-gray-400';
                      const pnl = s.pnl_usd || 0;
                      return (
                        <tr key={`${(s as any).date}-${s.id}`} className="hover:bg-white/5">
                          <td className="py-2 text-white text-sm font-mono">{(s as any).date}</td>
                          <td className="py-2 text-gray-300 text-sm capitalize">{s.session || '—'}</td>
                          <td className="py-2">
                            <div className="flex items-center space-x-1">
                              {s.direction === 'bearish'
                                ? <TrendingDown className="h-3.5 w-3.5 text-red-400" />
                                : <TrendingUp className="h-3.5 w-3.5 text-green-400" />
                              }
                              <span className="text-sm text-white">{s.direction === 'bearish' ? 'SHORT' : 'LONG'}</span>
                            </div>
                          </td>
                          <td className="py-2 text-white text-sm font-mono">
                            {s.entry_min.toLocaleString('en-US')}-{s.entry_max.toLocaleString('en-US')}
                          </td>
                          <td className="py-2 text-red-400 text-sm font-mono">{s.sl.toLocaleString('en-US')}</td>
                          <td className="py-2 text-green-400 text-sm font-mono">{s.tp.toLocaleString('en-US')}</td>
                          <td className="py-2 text-white text-sm">1:{s.rr_ratio.toFixed(1)}</td>
                          <td className="py-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}>
                              {outcome}
                            </span>
                          </td>
                          <td className={`py-2 text-sm font-mono text-right font-medium ${pnl > 0 ? 'text-green-400' : pnl < 0 ? 'text-red-400' : 'text-gray-500'}`}>
                            {pnl !== 0 ? `${pnl > 0 ? '+' : ''}$${pnl.toLocaleString('es-ES')}` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* TAB: Styles */}
        {activeTab === 'styles' && <AnalysisStyles />}

        {/* Edit User Modal */}
        {editingUser && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="bg-dark-card border border-white/10 rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">Editar Usuario</h2>
                <button onClick={() => setEditingUser(null)} className="p-2 hover:bg-white/5 rounded-lg transition-colors">
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300">Email</label>
                  <input type="text" value={editingUser.email} disabled className="input-field mt-1 opacity-50" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300">Limite de Analisis</label>
                  <input type="number" value={editingUser.analysisLimit}
                    onChange={(e) => setEditingUser({ ...editingUser, analysisLimit: parseInt(e.target.value) })}
                    min="0" className="input-field mt-1" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300">Nivel</label>
                  <select value={editingUser.userLevel}
                    onChange={(e) => setEditingUser({ ...editingUser, userLevel: e.target.value })}
                    className="input-field mt-1">
                    <option value="Trader">Trader</option>
                    <option value="Trader Pro">Trader Pro</option>
                  </select>
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300">Usuario Bloqueado</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" checked={editingUser.isBlocked}
                      onChange={(e) => setEditingUser({ ...editingUser, isBlocked: e.target.checked })}
                      className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-4 mt-6">
                <button onClick={() => setEditingUser(null)} className="btn-secondary">Cancelar</button>
                <button onClick={handleUpdateUser} disabled={loading} className="btn-primary flex items-center space-x-2">
                  {loading ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white/20 border-t-white"></div><span>Guardando...</span></>
                  ) : (
                    <><Save className="h-4 w-4" /><span>Guardar Cambios</span></>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete User Modal */}
        {deletingUser && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="bg-dark-card border border-white/10 rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">Eliminar Usuario</h2>
                <button onClick={() => setDeletingUser(null)} className="p-2 hover:bg-white/5 rounded-lg transition-colors">
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
              <div className="space-y-4">
                <div className="flex items-center space-x-2 text-yellow-500">
                  <AlertTriangle className="h-5 w-5" />
                  <p className="font-medium">Estas seguro?</p>
                </div>
                <p className="text-gray-300">
                  Estas seguro de que quieres eliminar al usuario <span className="text-white font-medium">{deletingUser.email}</span>?
                </p>
                <div className="bg-dark-overlay rounded-lg p-4 space-y-2">
                  <p className="text-gray-300">Esta accion eliminara:</p>
                  <ul className="list-disc list-inside text-gray-300">
                    <li>Todos los analisis del usuario</li>
                    <li>El diario de trading</li>
                    <li>Datos de embajadores</li>
                    <li>Configuraciones y preferencias</li>
                  </ul>
                  <p className="text-red-500 font-medium">Esta accion no se puede deshacer.</p>
                </div>
              </div>
              <div className="flex justify-end space-x-4 mt-6">
                <button onClick={() => setDeletingUser(null)} className="btn-secondary">Cancelar</button>
                <button onClick={handleConfirmDelete} className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded transition-colors flex items-center space-x-2">
                  <Trash2 className="h-4 w-4" /><span>Eliminar Usuario</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
